import styled from '@emotion/styled';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { LanguageConfig, LanguageConfigMap, LanguageKey } from '~/shared/internationalization';

const LanguageSelectContainer = styled.div({
    marginLeft: '1rem',
    position: 'relative',
});

const LanguageSelectButton = styled.div({
    alignItems: 'center',
    color: '#53565b',
    cursor: 'pointer',
    display: 'flex',
    fontWeight: 600,
    gap: 5,
    justifyContent: 'flex-start',
    padding: '10px 14px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
});

const LanguageSelectList = styled.ul<{ readonly expandUpward: boolean }>(({ expandUpward }) => ({
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    bottom: expandUpward ? '100%' : 'auto',
    listStyle: 'none',
    margin: 0,
    padding: '4px 0',
    position: 'absolute',
    top: expandUpward ? 'auto' : '100%',
    width: '6rem',
    zIndex: 1000,
}));

const LanguageSelectListItem = styled.li({
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
    color: '#53565b',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '8px 18px',
    transition: 'background-color 0.2s',
});

const SelectIcon: FunctionComponent = () => (
    // biome-ignore lint/a11y/noSvgWithoutTitle: suppressed due to migration
    <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
        <g id="chevron-down">
            <path
                d="M3.5 5.25L7 8.75L10.5 5.25"
                id="Icon"
                stroke="#B2B2B0"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.66667"
            />
        </g>
    </svg>
);

export const getLanguageShortened = (name: string): string => name.slice(0, 2).toUpperCase();

type LanguageSelectProps = {
    readonly availableLanguages: LanguageConfigMap;
    readonly selectedLanguage: string;
    readonly setSelectedLanguage: (language: string, key: LanguageKey) => void;
};

export const LanguageSelect: FunctionComponent<LanguageSelectProps> = ({
    availableLanguages,
    selectedLanguage,
    setSelectedLanguage,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [expandUpward, setExpandUpward] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleSelect = ([key, language]: [LanguageKey, LanguageConfig]): void => {
        setSelectedLanguage(language.code, key);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isOpen && is.null(dropdownRef.current) === false) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const viewportHeight = globalThis.innerHeight;
            const dropdownHeight = 150;
            const isDropdownTooLow = rect.bottom + dropdownHeight > viewportHeight;

            setExpandUpward(isDropdownTooLow);
        }
    }, [isOpen]);

    return (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
            <LanguageSelectContainer ref={dropdownRef}>
                <LanguageSelectButton onClick={() => setIsOpen(!isOpen)}>
                    {getLanguageShortened(selectedLanguage)}
                    <SelectIcon />
                </LanguageSelectButton>
                {isOpen && (
                    <LanguageSelectList expandUpward={expandUpward}>
                        {Object.entries(availableLanguages).map(([key, lang]: [LanguageKey, LanguageConfig]) => (
                            <LanguageSelectListItem key={key} onClick={() => handleSelect([key, lang])}>
                                {lang.name}
                            </LanguageSelectListItem>
                        ))}
                    </LanguageSelectList>
                )}
            </LanguageSelectContainer>
        </ClickAwayListener>
    );
};
